import { useDebounce } from "@hooks/use-debounce";
import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import useGzLog from "@hooks/useGzLog";
import { useInfiniteQuery } from "@tanstack/react-query";
import { defaultPageSize } from "@utils/queries";
import { getPagination, selectData, softDelete, supabase } from "@utils/serverClient";
import { undefinedToNull } from "lib/utils";
import { useMemo, useState } from "react";
import { InsertTemplate, UpdateTemplate } from "types";
import { Template, TemplateWithCategory } from "./types";

export const usePublicTemplates = () => {
  const { log } = useGzLog()
  const { user, organization } = useAuth()
  const isAdmin = user?.system_role === "admin";
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>();
  const orgId = organization?.id

  const [searchString, setSearchString] = useState<string>();
  // const debouncedSearchString = useDebounce(searchString, 500);

  const templateCategoryId = selectedCategoryId
  const queryKey = [
    "publicTemplates",
    // screenSize?.width,
    // screenSize?.height,
    templateCategoryId,
    searchString,
  ]

  const {
    status: templatesFetchStatus,
    data: templates,
    refetch: getTemplates,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    // refetchOnMount: false,
    queryKey,
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      // const aspect_ratio = screenSize
      //   ? getAspectRatio(screenSize.width, screenSize.height)
      //   : 0;

      const { from, to } = getPagination(pageParam, defaultPageSize);

      const query =
        templateCategoryId !== undefined
          ? selectData({
            table: "template",
            columns:
              "*, template_template_category!inner( *, template_category( * ) )",
          })
          : selectData({
            table: "template",
            columns:
              "*, template_template_category( *, template_category( * ) )",
          });

      query
        .is("org_id", null)
        .range(from, to)
        .order("updated_at", { ascending: false })
        .order("created_at", { ascending: false });

      if (templateCategoryId) {
        query.eq("template_template_category.category_id", templateCategoryId);
      }

      if ((searchString ?? "").length > 0) {
        query.ilike("name", `%${searchString ?? ""}%`);
      }

      // if (screenSize) {
      //   query
      //     .filter("aspect_ratio", "gte", aspect_ratio * 0.9)
      //     .filter("aspect_ratio", "lte", aspect_ratio * 1.1);
      // }

      if (!isAdmin) {
        query.eq("status", "published");
      }

      const { data, error } = await query;

      if (error || !data) return;
      return data;
    },
    getNextPageParam: (_lastGroup, groups) =>
      (_lastGroup ?? [])?.length < defaultPageSize ? undefined : groups.length,
  });

  const { mutate: createTemplate, isPending: isCreating } = useDataMutation({
    mutationFn: async (template: InsertTemplate) => {
      const { data, error } = await supabase
        .from("template")
        .insert(template)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to create template.",
    optimisticUpdateFn: (
      oldData: Template[] | undefined,
      newTemplate: InsertTemplate,
    ) => {
      const optimisticTemplate: Template = {
        ...undefinedToNull(newTemplate),
        id: `temp-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        org_id: orgId ?? "",
      };
      return [optimisticTemplate, ...(oldData || [])];
    },
    options: {
      onSuccess: (data) => {
        // log({
        //   eventtype: "PublicTemplateCreated",
        //   name: "PublicTemplateCreated",
        //   payload: {
        //     templateId: data?.id ?? "undefined",
        //     templateName: data?.name ?? "undefined",
        //   },
        // });
      },
      onError: (error, newTemplate) => {
        // log({
        //   eventtype: "PublicTemplateCreateFailed",
        //   name: "PublicTemplateCreateFailed",
        //   payload: {
        //     templateName: newTemplate.name ?? "undefined",
        //     errorMessage: String(error?.message || "Unknown error creating template"),
        //   },
        // });
      },
    }
  });

  const { mutate: updateTemplate, isPending: isUpdating } = useDataMutation({
    mutationFn: async (variables: UpdateTemplate) => {
      const { error, data } = await supabase
        .from("template")
        .update(variables)
        .eq("id", variables.id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update template.",
    optimisticUpdateFn: (oldData, variables) => {
      if (!oldData) return [];
      return oldData.map((item) =>
        item.id === variables.id ? { ...item, ...variables } : item,
      );
    },
    options: {
      onSuccess: (data) => {
        // if (!data) return
        // const _toUpdate = screens.filter((e) => e.playlist_id === data.id);
        // for (const screen of _toUpdate) {
        //   emitEvent("playlistChanged", screen.id);
        // }
        // log({
        //   eventtype: "PublicTemplateUpdated",
        //   name: "PublicTemplateUpdated",
        //   payload: {
        //     templateId: data?.id ?? "undefined",
        //     templateName: data?.name ?? "undefined",
        //   },
        // });
      },
      onError: (error, variables) => {
        // log({
        //   eventtype: "PublicTemplateUpdateFailed",
        //   name: "PublicTemplateUpdateFailed",
        //   payload: {
        //     templateId: variables.id,
        //     templateName: variables.name ?? "undefined",
        //     errorMessage: String(error?.message || "Unknown error updating template"),
        //   },
        // });
      },
    }
  });

  const { mutate: deleteTemplate } = useDataMutation({
    mutationFn: async (id: string) => {
      const { error } = await softDelete({
        table: "template",
      }).eq("id", id);
      if (error) {
        throw new Error(error.message);
      }
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete template.",
    optimisticUpdateFn: (oldData: Template[] | undefined, id) => {
      if (!oldData) return [];
      return oldData.filter((item) => item.id !== id);
    },
    options: {
      onSuccess: (_, id) => {
        log({
          eventtype: "PublicTemplateDeleted",
          name: "PublicTemplateDeleted",
          payload: { templateId: id },
        });
      },
      onError: (error, id) => {
        log({
          eventtype: "PublicTemplateDeleteFailed",
          name: "PublicTemplateDeleteFailed",
          payload: {
            templateId: id,
            errorMessage: String(error?.message || "Unknown error deleting template"),
          },
        });
      },
    }
  });

  const flatTemplates = templates?.pages.flat()?.filter(Boolean).map(t => t!) ?? [];

  return {
    templates: flatTemplates,
    deleteTemplate,
    templatesFetchStatus,
    getTemplates,
    createTemplate,
    isCreating,
    updateTemplate,
    isUpdating,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    selectedCategoryId,
    setSelectedCategoryId,
    searchString,
    setSearchString,
  }
};
